"use client";

import React, { useCallback, useEffect, useState } from "react";
import { AuthContext } from "./AuthContext";
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  getRedirectResult,
  type AuthProvider as FirebaseAuthProvider,
  type User,
} from "firebase/auth";
import { auth } from "@/lib/firebaseClient";
import { isSessionExpired, msUntilSessionExpiry } from "@/lib/sessionExpiry";

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isSupport, setIsSupport] = useState(false);
  const isAuthenticated = !!user;

  useEffect(() => {
    let isMounted = true;
    let redirectCheckComplete = false;
    let claimCheckComplete = false;

    // Helper to check if we can mark loading as complete
    const checkCanCompleteLoading = () => {
      if (isMounted && redirectCheckComplete && claimCheckComplete) {
        setIsInitialLoading(false);
      }
    };

    // Warn if authDomain doesn't match current origin
    if (import.meta.env.PROD) {
      const currentHost = window.location.hostname;
      const authDomain = auth.app.options.authDomain;
      if (authDomain && !authDomain.includes(currentHost)) {
        console.warn(
          `[AuthProvider] ⚠️ WARNING: authDomain (${authDomain}) doesn't match current host (${currentHost}). ` +
            `signInWithRedirect may not work correctly. Update VITE_FIREBASE_AUTH_DOMAIN to match your hosting domain.`
        );
      }
    }

    // Set up auth state listener first (so we don't miss any state changes)
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (isMounted) {
        setUser(currentUser);

        // Check for super-admin custom claim BEFORE marking loading as complete
        // This ensures routes have the correct isSuperAdmin value
        if (currentUser) {
          (async () => {
            try {
              const tokenResult = await currentUser.getIdTokenResult(true); // Force refresh to get latest claims
              const role = tokenResult.claims.role;
              if (isMounted) {
                setIsSuperAdmin(role === "super-admin");
                setIsSupport(role === "support");
              }
            } catch (error) {
              console.error(
                "[AuthProvider] Error checking custom claims:",
                error
              );
              if (isMounted) {
                setIsSuperAdmin(false);
                setIsSupport(false);
              }
            } finally {
              // Mark claim check as complete
              claimCheckComplete = true;
              // Check if we can mark loading as complete
              checkCanCompleteLoading();
            }
          })();
        } else {
          setIsSuperAdmin(false);
          setIsSupport(false);
          claimCheckComplete = true; // No claim check needed if no user
          checkCanCompleteLoading();
        }
      }
    });

    // Check for redirect result (for production signInWithRedirect)
    // This MUST complete before we allow the router to process routes
    const checkRedirectResult = async () => {
      try {
        await getRedirectResult(auth);
      } catch (error) {
        console.error(
          "[AuthProvider] ❌ Error getting redirect result:",
          error
        );
      } finally {
        // Mark redirect check as complete
        redirectCheckComplete = true;
        // Check if we can mark loading as complete
        checkCanCompleteLoading();
      }
    };

    checkRedirectResult();

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Absolute session lifetime (DRA-357). Firebase renews the ID token
  // indefinitely from a long-lived refresh token, so without this a session
  // ends only when someone signs out. Measured from auth_time, the last real
  // sign-in, so an open tab cannot extend itself.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const expire = () => {
      signOut(auth).finally(() => {
        // Full navigation rather than the SPA router: this is a security
        // boundary, and a reload guarantees a clean auth state on the way back.
        window.location.href = "/login?expired=1";
      });
    };

    void (async () => {
      try {
        const { authTime } = await user.getIdTokenResult();
        if (cancelled) return;
        if (isSessionExpired(authTime)) {
          expire();
          return;
        }
        // Fire while the tab stays open, not only on next load.
        timer = setTimeout(expire, msUntilSessionExpiry(authTime));
      } catch (error) {
        // A token we cannot read is not evidence of a valid session.
        console.error("[AuthProvider] Could not read auth_time; signing out", error);
        expire();
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [user]);

  const login = useCallback(async (provider: FirebaseAuthProvider) => {
    // We want popup for local development (including when using the Auth emulator),
    // but redirect for real production domains.
    const hostname =
      typeof window !== "undefined" ? window.location.hostname : "";

    const isProdHostname =
      hostname === "rmpa.rivermountpartners.com" ||
      hostname.endsWith(".rivermountpartners.com") ||
      hostname.endsWith(".web.app") ||
      hostname.endsWith(".firebaseapp.com");

    const shouldUseRedirect = isProdHostname;

    if (shouldUseRedirect) {
      // signInWithRedirect immediately redirects, so we don't await it
      // The redirect result will be handled by getRedirectResult in useEffect
      signInWithRedirect(auth, provider).catch((error) => {
        console.error(
          "[AuthProvider] ❌ Error during sign-in redirect:",
          error
        );
      });
    } else {
      try {
        await signInWithPopup(auth, provider);
      } catch (error) {
        console.error("[AuthProvider] ❌ Error during sign-in popup:", error);
        throw error;
      }
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isInitialLoading,
        isSuperAdmin,
        isSupport,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
